import { redirect } from '@tanstack/react-router';

import { buildDarkwingUrl } from '@/components/navigation/utils';
import { ROUTE_FEATURE_FLAGS } from '@/config/route-feature-flags';

import { identifyUserInLaunchDarkly } from './shared-utils';
import type { RouteGuardContext, RouteGuardParams } from './types';

/**
 * Creates a route guard that checks feature flags and redirects to Darkwing if needed
 * @param routeId - The route identifier (e.g., 'alarms', 'equipment')
 * @returns A beforeLoad function for TanStack Router
 */
export function createMigrationRouteGuard(routeId: string) {
  return async ({ context, params }: { context: RouteGuardContext; params: RouteGuardParams }) => {
    const { ldClient, auth } = context;
    const flagKey = ROUTE_FEATURE_FLAGS[routeId];

    // If no feature flag is configured, allow navigation
    if (!flagKey) {
      console.log(`[${routeId} Route] No feature flag configured, allowing navigation`);
      return;
    }

    // Check if we need to identify the user in LaunchDarkly
    if (ldClient && auth) {
      await identifyUserInLaunchDarkly(ldClient, auth, params, `${routeId} Route`);
    }

    let flagValue = false; // Default to redirect (safe default)

    if (ldClient) {
      try {
        // Get the feature flag value
        flagValue = ldClient.variation(flagKey, false);
        console.log(`[${routeId} Route] Feature flag ${flagKey} = ${flagValue}`);
      } catch (error) {
        console.error(`[${routeId} Route] Error evaluating flag:`, error);
        // Keep default of false (redirect to Darkwing) on error
      }
    } else {
      console.warn(`[${routeId} Route] No LaunchDarkly client available`);
    }

    // If flag is disabled, redirect to Darkwing
    if (!flagValue) {
      const darkwingUrl = buildDarkwingUrl(params.org, params.agent, routeId);
      console.log(`[${routeId} Route] Redirecting to Darkwing: ${darkwingUrl}`);

      // Use TanStack Router's redirect with href for external URLs
      try {
        throw redirect({
          href: darkwingUrl,
        });
      } catch (error) {
        console.error(`[${routeId} Route] Error redirecting to Darkwing:`, error);
        window.location.href = window.location.origin;
      }
    }

    console.log(`[${routeId} Route] Feature enabled in Gaia, allowing navigation`);
  };
}
